<template>
  <!-- Wave 16 — the Price Breakdown modal became a body-teleported DRAWER on staging (z 10035/10036,
       deliberately ABOVE centre modals at 10030 per AB#21862). The wizard opens from inside that
       drawer, so without its own layer it renders BEHIND it. Teleport to body inside a stacking
       context at 10050: above every drawer, below the teleported popup layer (10060) so Select /
       Dropdown menus keep working. -->
  <Teleport to="body">
  <div v-if="modelValue" class="fixed inset-0 z-[10050]">
  <Dialog
    title="Create credit note"
    :custom-title="true"
    :show-close-button="true"
    :show-footer="false"
    :lg="true"
    :md="false"
    :sm="false"
    custom-width-class="!max-w-[min(64rem,96vw)]"
    :loading="submitting"
    @close-event="close"
  >
    <template #title>
      <div class="flex min-w-0 flex-col gap-0.5 pr-2">
        <h4 class="text-lg font-semibold leading-tight text-ink">
          Create credit note
        </h4>
        <p class="flex flex-wrap items-center gap-1.5 text-sm font-medium text-ink-soft">
          <span>
            Order #{{ orderLabel }}
            <template v-if="invoiceLabel">
              · Invoice {{ invoiceLabel }}
            </template>
          </span>
          <!-- Finance feedback: the wizard never said whether the invoice is paid — the chip is in
               the header because it is the only spot visible in every wizard state. Same pill
               styling as the Price Breakdown's invoice statuses. Wave 16: title case ("Paid"), no
               `uppercase` — matches how the status reads everywhere else in the app. -->
          <span
            v-if="invoiceStatusChip"
            data-testid="invoice-status-chip"
            class="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide"
            :class="invoiceStatusChip.class"
          >
            {{ invoiceStatusChip.label }}
          </span>
        </p>
      </div>
    </template>

    <template #modal-content>
      <template v-if="!eligibility">
        <p class="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-900">
          Eligibility was not provided to the wizard. The picker should pass it through; check
          <code>SelectStripeInvoiceForCreditNoteModal</code>.
        </p>
      </template>

      <!-- Inline success state (US 5253 / Task 16179): replaces the form with a result card so the
           payment-path-specific copy (especially the finance@ instruction for non-card paid
           invoices) cannot be dismissed accidentally as a toast can. Card-paid never shows
           finance@; non-card always shows finance@; unpaid never mentions refunds. -->
      <template v-else-if="state === 'success' && submitResult">
        <div
          class="rounded-lg border px-4 py-4 shadow-sm"
          :class="successPanelClass"
          role="status"
          aria-live="polite"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-semibold"
              :class="successIconClass"
              aria-hidden="true"
            >
              ✓
            </span>
            <div class="min-w-0 flex-1">
              <h5 class="text-base font-semibold">
                {{ successHeadline }}
              </h5>
              <p class="mt-1 text-sm leading-relaxed">
                {{ settlementOutcomeMessage(submitResult.settlementOption, submitResult.paymentPath) }}
              </p>
              <dl class="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                <div class="flex items-baseline gap-2">
                  <dt class="font-medium text-current/80">Credit total</dt>
                  <dd class="font-semibold">
                    {{ formatMinor(submitResult.totalMinor) }}
                    {{ submitResult.currency }}
                  </dd>
                </div>
                <div v-if="submitResult.creditNoteId" class="flex items-baseline gap-2 truncate">
                  <dt class="font-medium text-current/80">Stripe credit note</dt>
                  <dd class="truncate font-mono text-[11px]">{{ submitResult.creditNoteId }}</dd>
                </div>
              </dl>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f]"
              @click="onSuccessDone"
            >
              Done
            </button>
          </div>
        </div>
      </template>

      <!-- Inline error state (US 5253 / Task 16179): shows Stripe / backend error message + retry
           button. Refund-failure path adds an actionable note pointing to Stripe; no success copy
           is shown for failed creates (UI never refreshes as if credited — emit only fires on
           genuine success). -->
      <template v-else-if="state === 'error' && submitError">
        <div
          class="rounded-lg border border-rose-200 bg-rose-50/90 px-4 py-4 text-rose-950 shadow-sm"
          role="alert"
          aria-live="assertive"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-200 font-semibold text-rose-900"
              aria-hidden="true"
            >
              !
            </span>
            <div class="min-w-0 flex-1">
              <h5 class="text-base font-semibold text-rose-950">
                Credit note was not created
              </h5>
              <p class="mt-1 text-sm leading-relaxed text-rose-950/95 break-words">
                {{ submitError.message }}
              </p>
              <details v-if="submitError.detail" class="mt-2 text-xs text-rose-900/80">
                <summary class="cursor-pointer select-none hover:underline">Technical detail</summary>
                <p class="mt-1 break-words font-mono text-[11px]">{{ submitError.detail }}</p>
              </details>
              <p
                v-if="submitError.isRefundFailure"
                class="mt-2 rounded-md border border-rose-300/70 bg-surface px-3 py-2 text-xs text-rose-900"
              >
                Stripe rejected the card refund. Verify the original payment in your Stripe
                dashboard — the charge may be too old, already refunded, or the payment source may
                no longer be valid. If a refund is still owed, issue it manually in Stripe.
              </p>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 bg-surface px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-surface-3"
              @click="close"
            >
              Cancel
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-[#219f84] bg-surface px-4 py-2 text-sm font-semibold text-[#219f84] shadow-sm transition hover:bg-[#219f84] hover:text-white"
              @click="onErrorRetry"
            >
              Edit &amp; retry
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <!-- Prior credit notes banner — visible when the invoice already has CN(s) against it.
             Full mode would try to credit the whole invoice and run into the cap; the wizard
             defaults to Partial in this case (see watch on props.modelValue / props.eligibility). -->
        <section
          v-if="eligibility.existing_credit_notes.length > 0"
          class="mb-4 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5 text-sm text-amber-950"
          role="note"
        >
          <p class="font-semibold">
            {{ eligibility.existing_credit_notes.length }} prior credit note{{ eligibility.existing_credit_notes.length === 1 ? '' : 's' }} against this invoice
          </p>
          <p class="mt-1 text-xs text-amber-950/95">
            Total already credited:
            <strong>
              {{ formatMinor(priorCreditedMinor) }} {{ eligibility.currency }}
            </strong>
            · Remaining creditable:
            <strong>
              {{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}
            </strong>.
            Switched to <strong>Partial</strong> mode — pick lines / quantities to fit the cap, or switch a line to <strong>By amount</strong> to credit a specific amount.
          </p>
          <!-- Collapsed by default (finance feedback) — the banner's one-line totals stay visible. -->
          <details class="mt-2 text-xs" data-testid="credit-note-history">
            <summary class="cursor-pointer select-none text-amber-900 hover:underline">
              Credit note history ({{ eligibility.existing_credit_notes.length }})
            </summary>
            <ul class="mt-1.5 space-y-2 pl-1 text-amber-950/95">
              <li
                v-for="cn in eligibility.existing_credit_notes"
                :key="cn.id"
                class="rounded-md border border-amber-200/60 bg-surface p-2"
              >
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <span class="font-semibold text-amber-950">
                    {{ formatMinor(cn.amount_minor) }} {{ eligibility.currency }}
                  </span>
                  <span v-if="cn.number" class="text-amber-900">· {{ cn.number }}</span>
                  <span v-if="cn.status" class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                    {{ cn.status }}
                  </span>
                  <!-- Settlement tag (AB#21269) — same merged-pill treatment as the Billing
                       documents ledger; nothing rendered when the settlement is unknown. -->
                  <span
                    v-if="historySettlementTag(cn)"
                    data-testid="history-settlement-tag"
                    class="rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                    :class="historySettlementTag(cn)!.class"
                    :title="historySettlementTag(cn)!.tooltip"
                  >
                    {{ historySettlementTag(cn)!.label }}
                  </span>
                  <span v-if="cn.created" class="text-amber-800/80">· {{ formatCnCreated(cn.created) }}</span>
                </div>
                <div class="mt-0.5 text-[10px]">
                  <code class="rounded bg-amber-100/60 px-1 font-mono text-[10px] text-amber-900">{{ cn.id }}</code>
                  <!-- AB#22842 — prefer the finance-facing internal reason over the raw Stripe
                   enum (CNs issued straight in Stripe only carry the latter). -->
                  <span v-if="cn.internal_reason || cn.reason" class="ml-2">
                    Reason:
                    <span class="font-medium">{{
                      cn.internal_reason
                        ? internalReasonLabel(cn.internal_reason)
                        : formatCnReason(cn.reason)
                    }}</span>
                  </span>
                </div>
                <p v-if="cn.memo" class="mt-1 text-[11px] italic text-amber-900/95">
                  “{{ cn.memo }}”
                </p>
                <p
                  v-if="cn.internal_note"
                  data-testid="history-internal-note"
                  class="mt-1 text-[11px] text-amber-900/95"
                >
                  Internal note: {{ cn.internal_note }}
                </p>
                <a
                  v-if="cn.pdf"
                  :href="cn.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Credit note PDF"
                  class="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
                >
                  Open PDF ↗
                </a>
              </li>
            </ul>
          </details>
        </section>

        <!-- Mode toggle -->
        <section class="mb-4">
          <div
            class="inline-flex rounded-lg border border-slate-200 bg-surface-2 p-1"
            role="tablist"
            aria-label="Credit note mode"
          >
            <button
              v-for="opt in modeOptions"
              :key="opt.value"
              type="button"
              role="tab"
              :aria-selected="mode === opt.value"
              class="rounded-md px-3 py-1.5 text-sm font-medium transition"
              :class="
                mode === opt.value
                  ? 'bg-surface text-[#219f84] shadow-sm ring-1 ring-slate-200'
                  : 'text-ink-soft hover:text-ink'
              "
              @click="switchMode(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <p class="mt-2 text-xs text-ink-mute">{{ modeHint }}</p>
        </section>

        <!-- Invoice lines -->
        <section
          v-if="eligibility.lines.length > 0"
          class="mb-5 rounded-lg border border-slate-200 bg-surface"
        >
          <header class="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <h5 class="text-sm font-semibold text-ink">Invoice lines</h5>
            <div class="flex items-center gap-2">
              <!-- Lives HERE (not in the free-form section) because switching a line to amount
                   entry is an Invoice-lines action — the free-form explainer only explains.
                   Also rendered in Full mode: the handler switches to Partial itself, so this is
                   the one visible amount-entry affordance while the per-row toggles are hidden. -->
              <button
                type="button"
                data-testid="free-form-auto-tax-use-amount-button"
                :disabled="!amountModeAvailable"
                :title="
                  amountModeAvailable
                    ? 'Switch the first eligible line to By amount and focus its Credit amount box'
                    : 'No line on this invoice can take an amount-based credit any more (Stripe basis lock).'
                "
                class="inline-flex items-center gap-1 rounded-md border border-[#219f84] px-2.5 py-1 text-xs font-medium text-[#219f84] transition hover:bg-[#219f84] hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-ink-mute disabled:hover:bg-transparent disabled:hover:text-ink-mute"
                @click="switchFirstEligibleRowToAmountMode"
              >
                Credit a specific amount
              </button>
              <span class="text-xs text-ink-mute">
                {{ eligibility.lines.length }} line{{ eligibility.lines.length === 1 ? '' : 's' }}
              </span>
            </div>
          </header>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[640px] table-fixed border-collapse text-sm">
              <colgroup>
                <col :style="{ width: COLUMN_WIDTHS.select }" />
                <col />
                <col :style="{ width: COLUMN_WIDTHS.unitPrice }" />
                <col :style="{ width: COLUMN_WIDTHS.invQty }" />
                <col :style="{ width: COLUMN_WIDTHS.qty }" />
                <col :style="{ width: COLUMN_WIDTHS.amount }" />
                <col :style="{ width: COLUMN_WIDTHS.lineTotal }" />
                <!-- Wave 15 — trailing gutter mirrors the free-form table's Remove column so the
                     To Credit figures of both tables stack pixel-exact (finance feedback). -->
                <col :style="{ width: COLUMN_WIDTHS.remove }" />
              </colgroup>
              <thead class="bg-surface-2 text-[11px] font-semibold uppercase tracking-wide text-ink-soft">
                <tr>
                  <th class="px-3 py-2 text-left" scope="col">
                    <!-- AB#22842 — select / unselect all (finance works with 20-40 line
                     invoices). Tri-state: checked = every selectable line, dash = some.
                     Hidden in Full mode, where selection is managed by the mode itself. -->
                    <input
                      v-if="mode === 'partial'"
                      type="checkbox"
                      data-testid="select-all-lines"
                      :checked="allSelectableRowsSelected"
                      :indeterminate="someSelectableRowsSelected && !allSelectableRowsSelected"
                      :disabled="selectableRowCount === 0"
                      :title="allSelectableRowsSelected ? 'Unselect all lines' : 'Select all lines'"
                      :aria-label="allSelectableRowsSelected ? 'Unselect all lines' : 'Select all lines'"
                      class="h-4 w-4 rounded border-slate-300 text-[#219f84] focus:ring-[#219f84] disabled:cursor-not-allowed"
                      @change="onSelectAllToggle(($event.target as HTMLInputElement).checked)"
                    />
                    <span class="sr-only">Select</span>
                  </th>
                  <th class="px-3 py-2 text-left" scope="col">Description</th>
                  <th
                    class="px-3 py-2 text-right tabular-nums"
                    scope="col"
                    title="Price per unit as invoiced (excl. tax)"
                  >
                    Unit price
                  </th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Inv. qty</th>
                  <th class="px-3 py-2 text-right" scope="col">Credit qty</th>
                  <th class="px-3 py-2 text-right" scope="col">Credit amount</th>
                  <th
                    class="px-3 py-2 text-right tabular-nums"
                    scope="col"
                    title="Amount this line contributes to the credit note, excluding tax - quantity x unit price in By-qty mode, your entered amount in By-amount mode."
                  >
                    To Credit (excl. tax)
                  </th>
                  <th class="px-3 py-2" scope="col">
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr
                  v-for="row in lineRows"
                  :key="row.id"
                  data-testid="line-row"
                  :data-line-id="row.id"
                  :class="[
                    row.selected ? '' : 'bg-surface-2 text-ink-mute',
                    row.remainingQty <= 0 ? 'opacity-60' : '',
                  ]"
                >
                  <td class="px-3 py-2 align-middle">
                    <input
                      type="checkbox"
                      :checked="row.selected"
                      :disabled="mode === 'full' || row.remainingQty <= 0"
                      :title="row.remainingQty <= 0 ? 'This line is already fully credited' : undefined"
                      class="h-4 w-4 rounded border-slate-300 text-[#219f84] focus:ring-[#219f84] disabled:cursor-not-allowed"
                      @change="onSelectToggle(row, ($event.target as HTMLInputElement).checked)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <div class="truncate text-ink" :class="row.selected ? '' : 'text-ink-mute'">
                      {{ row.description || '—' }}
                    </div>
                    <div class="font-mono text-[10px] text-ink-mute">{{ row.id }}</div>
                    <div
                      v-if="row.alreadyCreditedQty > 0"
                      class="mt-0.5 text-[10px] font-medium text-amber-700"
                    >
                      <template v-if="row.remainingQty <= 0">
                        Already fully credited ({{ row.alreadyCreditedQty }} of {{ row.quantity }})
                      </template>
                      <template v-else>
                        {{ row.alreadyCreditedQty }} of {{ row.quantity }} already credited
                      </template>
                    </div>
                    <!-- WS2 — per-row entry mode toggle; hidden in Full mode (everything stays
                         disabled there, same as before this change). -->
                    <div
                      v-if="mode !== 'full'"
                      class="mt-1.5 inline-flex rounded border border-slate-200 bg-surface-2 p-0.5 text-[10px]"
                      role="tablist"
                      :aria-label="`Entry mode for ${row.description || row.id}`"
                    >
                      <button
                        type="button"
                        role="tab"
                        data-testid="line-entry-mode-qty"
                        :aria-selected="row.entryMode === 'qty'"
                        :disabled="!row.selected || row.remainingQty <= 0 || row.lockedBasis === 'amount'"
                        :title="
                          row.lockedBasis === 'amount'
                            ? 'This line was previously credited by amount — Stripe locks its further credits to By amount.'
                            : undefined
                        "
                        class="rounded px-1.5 py-0.5 font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                        :class="row.entryMode === 'qty' ? 'bg-surface text-[#219f84] shadow-sm' : 'text-ink-mute hover:text-ink'"
                        @click="onEntryModeChange(row, 'qty')"
                      >
                        By qty
                      </button>
                      <button
                        type="button"
                        role="tab"
                        data-testid="line-entry-mode-amount"
                        :aria-selected="row.entryMode === 'amount'"
                        :disabled="!row.selected || row.remainingAmountMinor <= 0 || row.lockedBasis === 'quantity'"
                        :title="amountToggleTitle(row)"
                        class="rounded px-1.5 py-0.5 font-medium transition disabled:cursor-not-allowed disabled:opacity-50"
                        :class="row.entryMode === 'amount' ? 'bg-surface text-[#219f84] shadow-sm' : 'text-ink-mute hover:text-ink'"
                        @click="onEntryModeChange(row, 'amount')"
                      >
                        By amount
                      </button>
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums">
                    {{ formatMinor(row.unit_amount_minor) }}
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums">
                    <div>{{ row.quantity }}</div>
                    <div
                      v-if="row.alreadyCreditedQty > 0"
                      class="text-[10px] font-normal text-amber-700"
                    >
                      ({{ row.remainingQty }} left)
                    </div>
                  </td>
                  <td
                    class="px-3 py-2 align-middle"
                    :class="canActivateQtyMode(row) ? 'cursor-pointer' : ''"
                    :title="canActivateQtyMode(row) ? 'Click to switch this line to By qty' : undefined"
                    data-testid="line-qty-cell"
                    @click="onQtyCellClick(row)"
                  >
                    <input
                      type="number"
                      min="0"
                      step="1"
                      data-testid="line-qty-input"
                      :data-line-id="row.id"
                      :disabled="!row.selected || mode === 'full' || row.entryMode === 'amount' || row.remainingQty <= 0"
                      :value="row.creditQty"
                      class="w-full rounded border px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-mute"
                      :class="
                        row.invalid && row.entryMode === 'qty'
                          ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                          : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
                      "
                      @input="onQtyInput(row, ($event.target as HTMLInputElement).value)"
                    />
                    <p
                      v-if="row.invalid && row.entryMode === 'qty'"
                      data-testid="line-invalid-message"
                      class="mt-1 text-[10px] leading-tight text-rose-600"
                    >
                      {{ row.invalidMessage }}
                    </p>
                  </td>
                  <td
                    class="px-3 py-2 align-middle"
                    :class="canActivateAmountMode(row) ? 'cursor-pointer' : ''"
                    :title="canActivateAmountMode(row) ? 'Click to credit this line by amount instead' : undefined"
                    data-testid="line-amount-cell"
                    @click="onAmountCellClick(row)"
                  >
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      data-testid="line-amount-input"
                      :data-line-id="row.id"
                      :disabled="!row.selected || mode === 'full' || row.entryMode === 'qty' || row.remainingAmountMinor <= 0"
                      :value="row.creditAmountDisplay"
                      class="w-full rounded border px-2 py-1 text-right text-sm tabular-nums outline-none focus:ring-1 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-mute"
                      :class="
                        row.invalid && row.entryMode === 'amount'
                          ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                          : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
                      "
                      placeholder="—"
                      @input="onAmountInput(row, ($event.target as HTMLInputElement).value)"
                    />
                    <p
                      v-if="row.invalid && row.entryMode === 'amount'"
                      data-testid="line-invalid-message"
                      class="mt-1 text-[10px] leading-tight text-rose-600"
                    >
                      {{ row.invalidMessage }}
                    </p>
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums font-semibold text-ink">
                    {{ formatMinor(lineTotalMinor(row)) }}
                  </td>
                  <td class="px-3 py-2" aria-hidden="true" />
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Stripe basis-lock note — lives with the Invoice lines it describes (moved out of the
               free-form section per finance feedback): when every line is quantity-locked, say so
               right where the entry-mode toggles are. -->
          <p
            v-if="!amountModeAvailable && lineRows.length > 0"
            data-testid="invoice-lines-basis-lock-note"
            class="border-t border-slate-100 px-3 py-2 text-xs text-amber-700"
          >
            Every line on this invoice has already been credited by quantity, and Stripe locks a
            line to the credit basis first used on it — only further <strong>quantity</strong>
            credits are possible here.
          </p>
        </section>

        <!-- Wave 15 — totals block (finance feedback): the grand total sits directly under the
             To Credit column it sums, on the same shared colgroup, like a document footer. Pure
             client-side arithmetic — always live, never waits on the Stripe preview (the footer
             keeps the authoritative tax-inclusive figure). AB#22842: free-form lines are gone,
             so the per-section subtotals went with them. -->
        <section v-if="totalMinor > 0" class="mb-5 -mt-3">
          <table class="w-full min-w-[640px] table-fixed border-collapse text-sm">
            <colgroup>
              <col :style="{ width: COLUMN_WIDTHS.select }" />
              <col />
              <col :style="{ width: COLUMN_WIDTHS.unitPrice }" />
              <col :style="{ width: COLUMN_WIDTHS.invQty }" />
              <col :style="{ width: COLUMN_WIDTHS.qty }" />
              <col :style="{ width: COLUMN_WIDTHS.amount }" />
              <col :style="{ width: COLUMN_WIDTHS.lineTotal }" />
              <col :style="{ width: COLUMN_WIDTHS.remove }" />
            </colgroup>
            <tbody>
              <tr data-testid="totals-grand-total" class="border-t border-slate-300">
                <td colspan="6" class="px-3 py-1.5 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">
                  Grand total (excl. tax)
                </td>
                <td class="px-3 py-1.5 text-right tabular-nums text-base font-bold text-ink">
                  {{ formatMinor(totalMinor) }}
                </td>
                <td aria-hidden="true" />
              </tr>
            </tbody>
          </table>
        </section>

        <!-- AB#22842 — reason (internal catalog) + internal note + memo. The Stripe reason is
         derived server-side from the internal reason and never shown or chosen here. -->
        <section class="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label class="flex flex-col gap-1.5 text-sm text-ink">
            <span class="font-medium">Reason <span class="text-xs font-normal text-ink-mute">(optional)</span></span>
            <select
              v-model="internalReason"
              data-testid="internal-reason-select"
              class="rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm shadow-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
            >
              <option :value="null">— Select —</option>
              <option v-for="r in INTERNAL_CREDIT_NOTE_REASONS" :key="r.value" :value="r.value">{{ r.label }}</option>
            </select>
            <span
              v-if="selectedReasonExample"
              data-testid="internal-reason-example"
              class="text-xs text-ink-mute"
            >
              {{ selectedReasonExample }}
            </span>
          </label>
          <label class="flex flex-col gap-1.5 text-sm text-ink md:col-span-1">
            <span class="font-medium">
              Internal note
              <span class="text-xs font-normal" :class="internalNoteMissing ? 'text-rose-600' : 'text-ink-mute'">
                {{ internalReason === 'other' ? '(required for Other)' : '(optional)' }}
              </span>
            </span>
            <textarea
              v-model="internalNote"
              data-testid="internal-note-input"
              rows="2"
              maxlength="5000"
              class="rounded-lg border bg-surface px-3 py-2 text-sm shadow-sm outline-none focus:ring-1"
              :class="
                internalNoteMissing
                  ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-400/30'
                  : 'border-slate-300 focus:border-[#219f84] focus:ring-[#219f84]/30'
              "
              placeholder="Visible to finance only"
            />
            <span class="text-xs text-ink-mute">
              Internal only: saved with the credit note and shown in Stripe's dashboard as
              metadata. Never on the credit note PDF or anything the customer sees.
            </span>
          </label>
          <label class="flex flex-col gap-1.5 text-sm text-ink md:col-span-2">
            <span class="font-medium">Memo <span class="text-xs font-normal text-ink-mute">(optional)</span></span>
            <textarea
              v-model="memo"
              rows="2"
              maxlength="5000"
              class="rounded-lg border border-slate-300 bg-surface px-3 py-2 text-sm shadow-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
              placeholder="Notes for the credit note (sent to Stripe)"
            />
            <span class="text-xs text-ink-mute">
              The memo prints on the credit note PDF the customer can see. Use the internal
              note above for anything internal.
            </span>
          </label>
        </section>

        <!-- AB#22842 — settlement choice (Nicolai's matrix): where the money goes. Only the
         options valid for how the invoice was paid are offered; the unpaid path has no
         choice (the credit note reduces the amount due). -->
        <section
          v-if="settlementOptions.length > 0"
          data-testid="settlement-section"
          class="mb-5 rounded-lg border border-slate-200 bg-surface px-3 py-3"
        >
          <h5 class="text-sm font-semibold text-ink">Where does the money go?</h5>
          <div class="mt-2 flex flex-col gap-2">
            <label
              v-for="opt in settlementOptions"
              :key="opt"
              class="flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 text-sm transition"
              :class="
                settlementOption === opt
                  ? 'border-[#219f84] bg-[#219f84]/5'
                  : 'border-slate-200 hover:border-slate-300'
              "
            >
              <input
                v-model="settlementOption"
                type="radio"
                name="cn-settlement-option"
                :value="opt"
                :data-testid="`settlement-option-${opt}`"
                class="mt-0.5 h-4 w-4 border-slate-300 text-[#219f84] focus:ring-[#219f84]"
              />
              <span class="flex min-w-0 flex-col">
                <span class="font-medium text-ink">{{ settlementChoiceCopy(opt).label }}</span>
                <span class="text-xs text-ink-soft">{{ settlementChoiceCopy(opt).description }}</span>
              </span>
            </label>
          </div>
        </section>
        <!-- Unpaid path: crediting a partially paid invoice past what is still owed settles the
         slice beyond the amount due out-of-band — announced, never blocked (AB#22842). -->
        <p
          v-else-if="unpaidOverflowMinor > 0"
          data-testid="unpaid-overflow-note"
          class="mb-5 rounded-md border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-xs text-amber-950"
        >
          {{ formatMinor(unpaidOverflowMinor) }} {{ eligibility.currency }} of this credit is
          more than what is still owed on this invoice. That part will be recorded as credited
          outside of Stripe (no money moves for it); the rest reduces the amount due.
        </p>

        <!-- Summary + submit -->
        <section
          class="sticky bottom-0 -mx-1 rounded-lg border border-slate-200 bg-surface-2 px-3 py-3 backdrop-blur"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span class="text-ink-soft">
                Remaining creditable:
                <span class="font-semibold text-ink" data-testid="remaining-creditable">
                  {{ formatMinor(remainingAfterDraftMinor) }} {{ eligibility.currency }}
                </span>
              </span>

              <!-- WS1 — preview-backed totals (feature-detected backend). -->
              <template v-if="previewSupported === true">
                <!-- Wave 15 — never render a concrete figure that doesn't match the inputs: any
                     invalid row hides the number entirely (a stale total mid-edit reads as truth). -->
                <span v-if="preview && !anyRowInvalid" data-testid="preview-total" class="text-ink-soft">
                  Credit total (incl. tax):
                  <span
                    class="font-semibold"
                    :class="effectiveIsOverCap ? 'text-rose-600' : 'text-ink'"
                  >
                    {{ formatMinor(preview.total_minor) }} {{ preview.currency }}
                  </span>
                  <span class="ml-1 text-xs text-ink-mute">
                    (excl. tax {{ formatMinor(preview.total_excluding_tax_minor) }} · tax {{ formatMinor(preview.tax_minor) }})
                  </span>
                  <span v-if="previewState === 'pending'" class="ml-1 text-[11px] italic text-ink-mute">
                    Updating…
                  </span>
                </span>
                <span v-else data-testid="preview-placeholder" class="text-xs italic text-ink-mute">
                  {{
                    anyRowInvalid
                      ? 'Fix the highlighted line to update the total'
                      : previewState === 'error'
                        ? 'Preview unavailable'
                        : 'Calculating total…'
                  }}
                </span>
              </template>

              <!-- Legacy fallback — pre-preview backend (404 feature-detect) or still detecting. -->
              <span v-else class="text-ink-soft">
                Credit note total (excl. tax):
                <span
                  class="font-semibold"
                  :class="isOverCap ? 'text-rose-600' : 'text-ink'"
                >
                  {{ formatMinor(totalMinor) }} {{ eligibility.currency }}
                </span>
                <span class="text-xs text-ink-mute">(Tax is added automatically by Stripe)</span>
              </span>

              <span
                v-if="footerChipLabel"
                data-testid="footer-chip"
                :title="footerChipTooltip"
                class="inline-flex items-center rounded-md border border-slate-300 bg-surface px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-ink-soft"
              >
                {{ footerChipLabel }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="rounded-md border border-slate-300 bg-surface px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-surface-3"
                :disabled="submitting"
                @click="close"
              >
                Cancel
              </button>
              <button
                v-if="state !== 'confirming'"
                type="button"
                data-testid="create-credit-note-button"
                class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canSubmit"
                @click="onCreateClick"
              >
                <span
                  v-if="submitting"
                  class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"
                />
                Create credit note
              </button>
            </div>
          </div>

          <p
            v-if="previewSupported === true && previewState === 'error'"
            class="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium text-rose-600"
          >
            <span data-testid="preview-error-message">
              {{ previewErrorInfo?.message ?? 'Could not calculate a preview for this credit note.' }}
            </span>
            <button
              type="button"
              data-testid="preview-retry-button"
              class="rounded border border-rose-300 bg-surface px-2 py-0.5 text-[11px] font-medium text-rose-700 transition hover:bg-rose-50"
              @click="schedulePreview(true)"
            >
              Retry
            </button>
          </p>
          <p v-else-if="effectiveIsOverCap" class="mt-2 text-xs font-medium text-rose-600">
            Credit note total exceeds the invoice’s remaining creditable
            ({{ formatMinor(remainingCreditableMinor) }} {{ eligibility.currency }}).
            Reduce a line to proceed.
          </p>
          <p v-else-if="internalNoteMissing" class="mt-2 text-xs font-medium text-rose-600">
            Add an internal note: it is required when the reason is Other.
          </p>
          <p v-else-if="noLineToCredit" class="mt-2 text-xs text-ink-mute">
            Select at least one invoice line to enable submit.
          </p>
          <!-- AB#23070: lines ARE selected, but a discount line outweighs them. -->
          <p
            v-else-if="totalMinor <= 0"
            data-testid="non-positive-total-hint"
            class="mt-2 text-xs font-medium text-rose-600"
          >
            The credit note total must be above 0. A discount line can only be credited together
            with the lines it reduces.
          </p>

          <!-- WS6 — confirm step for card_refund / out_of_band; unpaid submits directly. -->
          <div
            v-if="state === 'confirming'"
            data-testid="confirm-strip"
            class="mt-3 rounded-md border border-amber-300 bg-amber-50/80 px-3 py-3 text-sm text-amber-950"
          >
            <p data-testid="confirm-statement" class="leading-relaxed">{{ confirmStatement }}</p>
            <div class="mt-3 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                data-testid="confirm-back-button"
                class="rounded-md border border-slate-300 bg-surface px-3 py-2 text-sm font-medium text-ink-soft transition hover:bg-surface-3"
                :disabled="submitting"
                @click="state = 'idle'"
              >
                Back
              </button>
              <button
                type="button"
                data-testid="confirm-submit-button"
                class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canSubmit"
                @click="submit"
              >
                <span
                  v-if="submitting"
                  class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"
                />
                {{ confirmButtonLabel }}
              </button>
            </div>
          </div>
        </section>
      </template>
    </template>
  </Dialog>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';

import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import {
  CREDIT_NOTE_REASONS,
  INTERNAL_CREDIT_NOTE_REASONS,
  type CreditNoteEligibility,
  type CreditNoteErrorInfo,
  type CreditNoteInvoiceLine,
  type CreditNotePreview,
  type CreditNoteSettlementOption,
  type InternalCreditNoteReason,
  type LineEntryMode,
  amountToMinorUnits,
  creditNoteErrorFromResponse,
  formatMinorAsDisplay,
  internalReasonLabel,
  isLikelyRefundFailure,
  minorUnitsToAmount,
  parseQtyInput,
  paymentPathPreCreateStatement,
  previewCreditNote,
  settlementChoiceCopy,
  settlementOptionsForPath,
  settlementOutcomeMessage,
  settlementPreCreateStatement,
  validateLineEntry,
} from '@/utils/stripeCreditNote';
import { creditNoteSettlement } from '@/utils/billingDocuments';

type Mode = 'full' | 'partial';
/** WS6 adds `'confirming'` — the inline confirm strip shown before card_refund/out_of_band creates. */
type WizardState = 'idle' | 'confirming' | 'submitting' | 'success' | 'error';
/** WS1 — preview request lifecycle, independent of `WizardState`. */
type PreviewState = 'idle' | 'pending' | 'fresh' | 'error';

/**
 * WS4 / wave 15 — single source of truth for the columns shared by the invoice-lines table, the
 * free-form table AND the totals block: all three render the identical <colgroup> (same names,
 * same widths, same positions — finance feedback), so no column can drift between sections.
 */
const COLUMN_WIDTHS = {
  select: '2rem',
  // Finance feedback: Unit price / Inv. qty were unconstrained flex columns sharing the slack
  // with Description, ballooning the gap between them — fixed compact widths hand ALL slack to
  // Description. Trailing shared columns keep aligning with the free-form table (table-layout:
  // fixed makes their x-position = total minus the trailing fixed widths, unchanged by this).
  unitPrice: '6rem',
  invQty: '5rem',
  qty: '7rem',
  amount: '8rem',
  lineTotal: '7rem',
  remove: '2.5rem',
} as const;

interface SubmitResult {
  paymentPath: string | null;
  /** AB#22842 — which settlement was actually applied (echoed by the backend). */
  settlementOption: string | null;
  totalMinor: number;
  currency: string;
  creditNoteId: string | null;
  rawCreditRow: Record<string, unknown>;
}

interface SubmitError {
  message: string;
  /** Raw backend `errors.detail`, when present — shown behind a collapsed "Technical detail". */
  detail: string | null;
  isRefundFailure: boolean;
}

interface LineRow {
  id: string;
  description: string | null;
  unit_amount_minor: number;
  quantity: number;
  /** Already credited via prior CNs (sum across `existing_credit_notes`). */
  alreadyCreditedQty: number;
  /** Maximum creditable qty for this row right now (`quantity - alreadyCreditedQty`). */
  remainingQty: number;
  /** Maximum creditable amount in minor units (`amount_minor - already_credited_amount_minor`). */
  remainingAmountMinor: number;
  /**
   * Stripe locks each invoice line to the credit basis first used against it (`credit_basis` from
   * eligibility): a qty-credited line can only ever be credited by qty again, and vice versa.
   * `null` = never credited, both modes allowed. Drives disabled state + tooltip on the toggle.
   */
  lockedBasis: 'quantity' | 'amount' | null;
  selected: boolean;
  /**
   * WS2 — per-row entry mode. Exactly one of qty/amount is live at a time; the other input is
   * disabled and its value is cleared on switch so it can't leak stale state into the payload.
   * Defaults to `'qty'`.
   */
  entryMode: LineEntryMode;
  /** Credit quantity entered by the user. NOT clamped at `remainingQty` — see `invalid`. */
  creditQty: number;
  /** Explicit credit amount in minor units; takes precedence over qty when `entryMode === 'amount'`. */
  creditAmountMinor: number | null;
  /** Display string for the amount input, kept separate from numeric state for editing UX. */
  creditAmountDisplay: string;
  /**
   * Reject-over-cap (WS2): true when the active field's parsed value exceeds this row's
   * remaining cap. The raw input text is never clamped — this flag drives the red border +
   * inline message and blocks submit/preview instead.
   */
  invalid: boolean;
  /** Human message shown under the active input while `invalid` is true. */
  invalidMessage: string | null;
}

const props = defineProps<{
  modelValue: boolean;
  orderId: number;
  /** Local `order_item_invoices.id` — required by the backend create-credit-note endpoint. */
  orderItemInvoiceId: number;
  orderLabel: string | number;
  invoiceLabel: string | null;
  eligibility: CreditNoteEligibility | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'credit-note-created', payload: Record<string, unknown>): void;
}>();

const mode = ref<Mode>('full');
// AB#22842 — the finance-facing internal reason (mapped to the Stripe reason server-side) and
// the finance-only note (required when the reason is Other; stored + Stripe metadata only).
const internalReason = ref<InternalCreditNoteReason | null>(null);
const internalNote = ref('');
/** AB#22842 — finance's settlement choice; defaulted to the first allowed option on open. */
const settlementOption = ref<CreditNoteSettlementOption | null>(null);
const memo = ref('');
const lineRows = ref<LineRow[]>([]);
/** Wizard state machine — drives which surface the template renders. */
const state = ref<WizardState>('idle');
const submitResult = ref<SubmitResult | null>(null);
const submitError = ref<SubmitError | null>(null);
/** Regenerated on wizard open; reused on retry unless the form changed since last attempt. */
const idempotencyKey = ref('');
/** True when the user has edited the form after a submit attempt — drives smart key rotation. */
const formDirtyAfterLastSubmit = ref(false);

// ---------------------------------------------------------------------------
// WS1 — credit note preview. `previewSupported` is feature-detected once per modal open: `null`
// until the first request resolves/404s, `false` permanently for that open on a 404 (legacy
// backend — falls back to the pre-tax footer + old aggregate-cap submit gating), `true` once a
// preview request has succeeded or come back with a real (non-404) error.
// ---------------------------------------------------------------------------
const preview = ref<CreditNotePreview | null>(null);
const previewState = ref<PreviewState>('idle');
const previewErrorInfo = ref<CreditNoteErrorInfo | null>(null);
const previewSupported = ref<boolean | null>(null);
let previewSequence = 0;
let previewDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Submitting view is derived from state — used by the existing template + disabled props. */
const submitting = computed(() => state.value === 'submitting');

// ---------------------------------------------------------------------------
// AB#22842 — settlement options, internal reason helpers, local remaining creditable,
// select/unselect-all.
// ---------------------------------------------------------------------------

/** The settlements finance may choose for this invoice: backend-provided when available,
 * otherwise derived from the payment path (older backends). Empty on the unpaid path. */
const settlementOptions = computed<CreditNoteSettlementOption[]>(() => {
  const fromBackend = props.eligibility?.settlement_options;
  if (Array.isArray(fromBackend) && fromBackend.length > 0) return fromBackend;
  return settlementOptionsForPath(props.eligibility?.payment_path ?? null);
});

/** Example text under the reason dropdown for the selected internal reason. */
const selectedReasonExample = computed(() => {
  const match = INTERNAL_CREDIT_NOTE_REASONS.find((r) => r.value === internalReason.value);
  return match ? match.example : '';
});

/**
 * Local remaining-creditable figure (Elisabete's point 8): seeded from eligibility on open and
 * decremented by the created total on success, so the footer updates the moment a credit lands
 * instead of showing the open-time figure until the next full reopen.
 */
const remainingCreditableMinor = ref(0);

/** Unpaid path: the slice of the current preview that exceeds what is still owed (settled
 * out-of-band at create time). 0 on paid paths and on fully unpaid invoices. */
const unpaidOverflowMinor = computed(() => {
  if (props.eligibility?.payment_path !== 'unpaid') return 0;
  const fromPreview = preview.value?.out_of_band_overflow_minor ?? preview.value?.post_payment_amount_minor;
  return Number(fromPreview ?? 0) || 0;
});

/** Rows the select-all control may act on (fully credited lines stay untouchable). */
const selectableRowCount = computed(
  () =>
    lineRows.value.filter((row) =>
      row.lockedBasis === 'amount' || row.entryMode === 'amount'
        ? row.remainingAmountMinor > 0
        : row.remainingQty > 0
    ).length
);
const selectedSelectableRowCount = computed(
  () =>
    lineRows.value.filter(
      (row) =>
        row.selected &&
        (row.lockedBasis === 'amount' || row.entryMode === 'amount'
          ? row.remainingAmountMinor > 0
          : row.remainingQty > 0)
    ).length
);
const allSelectableRowsSelected = computed(
  () => selectableRowCount.value > 0 && selectedSelectableRowCount.value === selectableRowCount.value
);
const someSelectableRowsSelected = computed(() => selectedSelectableRowCount.value > 0);

/** AB#22842 — select / unselect every selectable line through the same per-row toggle path, so
 * all its guards (fully credited, prefills, preview rescheduling) apply unchanged. */
function onSelectAllToggle(checked: boolean): void {
  for (const row of lineRows.value) {
    const selectable =
      row.lockedBasis === 'amount' || row.entryMode === 'amount'
        ? row.remainingAmountMinor > 0
        : row.remainingQty > 0;
    if (!selectable) continue;
    if (row.selected === checked) continue;
    onSelectToggle(row, checked);
  }
}

const modeOptions: Array<{ value: Mode; label: string }> = [
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
];

const modeHint = computed(() =>
  mode.value === 'full'
    ? 'Credit every line at its full invoiced quantity.'
    : 'Pick the lines you want to credit and edit quantity or amount per line.',
);

const paymentPathHint = computed(() => {
  const path = props.eligibility?.payment_path;
  if (path === 'card_refund') return 'Card refund';
  if (path === 'out_of_band') return 'Out-of-band (finance follow-up)';
  // Finance feedback: "Reduces amount due" read as accounting-speak — say what changes instead.
  if (path === 'unpaid') return 'Reduces invoice balance';
  return '';
});

/** Hover explanation for the payment-path pill — every path says what happens to the money. */
const paymentPathTooltip = computed(() => {
  const path = props.eligibility?.payment_path;
  if (path === 'card_refund') {
    return 'This invoice was paid by card — creating a credit note refunds the credited amount to the original card immediately.';
  }
  if (path === 'out_of_band') {
    return 'This invoice was paid outside of card processing — the credit is recorded for manual settlement by finance.';
  }
  if (path === 'unpaid') {
    return 'This invoice hasn’t been paid — the credit lowers what the customer owes. No money is refunded.';
  }
  return undefined;
});

/** AB#22842: once a settlement can be (and is) chosen, the footer chip follows the CHOICE — a
 * CARD REFUND chip next to a chosen bank refund read as a contradiction. Paths without a
 * settlement choice (unpaid, legacy backend) keep the payment-path hint. */
const footerChipLabel = computed(() => {
  const settlement = settlementOption.value;
  if (settlementOptions.value.length > 0 && settlement) {
    if (settlement === 'card_refund') return 'Card refund';
    if (settlement === 'bank_refund') return 'Bank refund to follow';
    return 'No refund';
  }
  return paymentPathHint.value;
});

const footerChipTooltip = computed(() => {
  const settlement = settlementOption.value;
  if (settlementOptions.value.length > 0 && settlement) {
    return settlementChoiceCopy(settlement).description;
  }
  return paymentPathTooltip.value;
});

/**
 * Invoice payment-status chip for the wizard header (finance feedback: the wizard never said
 * whether the invoice was paid). Mirrors the Price Breakdown's status pills; `partially_paid`
 * wins over the raw `open` status because that nuance changes what a credit can do.
 */
const invoiceStatusChip = computed<{ label: string; class: string } | null>(() => {
  const status = props.eligibility?.invoice?.status;
  if (!status) return null;
  if (status === 'paid') {
    return { label: 'Paid', class: 'border-emerald-200 bg-emerald-50 text-emerald-800' };
  }
  if (status === 'open') {
    return props.eligibility?.partially_paid
      ? { label: 'Partially paid', class: 'border-amber-200 bg-amber-50 text-amber-800' }
      : { label: 'Open — unpaid', class: 'border-amber-200 bg-amber-50 text-amber-800' };
  }
  return { label: status, class: 'border-slate-200 bg-surface-2 text-ink-soft capitalize' };
});

/** Sum of prior CN totals against this invoice — surfaced in the "Prior credit notes" banner. */
const priorCreditedMinor = computed(() => {
  const cns = props.eligibility?.existing_credit_notes ?? [];
  let sum = 0;
  for (const cn of cns) {
    const v = Number(cn.amount_minor ?? 0);
    if (Number.isFinite(v)) sum += v;
  }
  return sum;
});

/**
 * Success panel styling shifts to amber when finance still has work to do (AB#22842: a chosen
 * manual bank refund, or the legacy out-of-band path with no recorded choice) — green would imply
 * the money already moved. Card refund / no refund / unpaid stay green.
 */
const successNeedsFollowUp = computed(() => {
  const settlement = submitResult.value?.settlementOption ?? null;
  if (settlement === 'bank_refund') return true;
  if (settlement === null && submitResult.value?.paymentPath === 'out_of_band') return true;
  return false;
});

const successPanelClass = computed(() =>
  successNeedsFollowUp.value
    ? 'border-amber-200 bg-amber-50/90 text-amber-950'
    : 'border-emerald-200 bg-emerald-50/90 text-emerald-950'
);

const successIconClass = computed(() =>
  successNeedsFollowUp.value ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'
);

const successHeadline = computed(() => {
  if (successNeedsFollowUp.value) return 'Credit note created: bank refund still to make';
  switch (submitResult.value?.settlementOption ?? submitResult.value?.paymentPath) {
    case 'card_refund':
      return 'Credit note created & refund issued';
    case 'no_refund':
      return 'Credit note created (no refund)';
    case 'unpaid':
      return 'Credit note applied to invoice';
    default:
      return 'Credit note created';
  }
});

function makeUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildLineRow(line: CreditNoteInvoiceLine): LineRow {
  const alreadyCredited = Number(line.already_credited_qty ?? 0);
  const remainingQty = Number(line.remaining_qty ?? line.quantity);
  const remainingAmount = Number(line.remaining_amount_minor ?? line.amount_minor);
  const lockedBasis = line.credit_basis ?? null;
  // Amount-locked lines (Stripe basis lock) can never be credited by qty again — open them in
  // amount mode pre-filled with the remaining amount, mirroring the qty default's "what's left".
  const opensInAmountMode = lockedBasis === 'amount';
  const currency = line.currency || 'USD';
  return {
    id: line.id,
    description: line.description,
    unit_amount_minor: line.unit_amount_minor,
    quantity: line.quantity,
    alreadyCreditedQty: alreadyCredited,
    remainingQty,
    remainingAmountMinor: remainingAmount,
    lockedBasis,
    // Auto-deselect lines with nothing left to credit in their (locked) basis.
    selected: opensInAmountMode ? remainingAmount > 0 : remainingQty > 0,
    // Default entry mode is qty (WS2); pre-fill with REMAINING qty (not original quantity) so
    // the wizard reflects what's left to credit after prior CNs.
    entryMode: opensInAmountMode ? 'amount' : 'qty',
    creditQty: opensInAmountMode ? 0 : remainingQty,
    creditAmountMinor: opensInAmountMode && remainingAmount > 0 ? remainingAmount : null,
    creditAmountDisplay:
      opensInAmountMode && remainingAmount > 0
        ? String(minorUnitsToAmount(remainingAmount, currency))
        : '',
    invalid: false,
    invalidMessage: null,
  };
}

function resetRowsFromEligibility(): void {
  const lines = props.eligibility?.lines ?? [];
  lineRows.value = lines.map(buildLineRow);
  // Wave 15 — the per-line prefills above only respect each LINE's remaining; when prior CNs
  // exist the invoice-level remaining creditable can be smaller, so clamp the defaults to it.
  clampAllPrefillsToInvoiceCap();
  internalReason.value = null;
  internalNote.value = '';
  // AB#22842 — default the settlement to the first allowed option (card-paid: refund to card;
  // bank/cash paid: refund manually by bank), matching pre-choice behavior.
  settlementOption.value = settlementOptions.value[0] ?? null;
  // AB#22842 — remaining creditable is tracked locally so a successful create updates the figure
  // immediately (Elisabete's point 8: the footer never moved after a partial credit).
  remainingCreditableMinor.value = props.eligibility?.remaining_creditable_minor ?? 0;
  memo.value = '';
  state.value = 'idle';
  submitResult.value = null;
  submitError.value = null;
  formDirtyAfterLastSubmit.value = false;
  // WS1 — feature-detect the preview endpoint fresh on every open (backend may have been
  // deployed/rolled back between opens); kick off the first preview immediately.
  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
    previewDebounceTimer = null;
  }
  previewSupported.value = null;
  preview.value = null;
  previewState.value = 'idle';
  previewErrorInfo.value = null;
  schedulePreview(true);
}

function switchMode(next: Mode): void {
  if (mode.value === next) return;
  mode.value = next;
  if (next === 'full') {
    // Full mode in the "after-prior-CN" case still means "credit each line at its remaining
    // quantity" — credit-already lines stay unselected so submit cap matches remaining_creditable.
    // Full mode also always uses qty entry (the per-row toggle is hidden while mode === 'full'),
    // EXCEPT amount-locked lines (Stripe basis lock) which can only be credited by amount — those
    // get their full remaining amount instead, so Full still means "everything that's left".
    const currency = props.eligibility?.currency ?? 'USD';
    for (const row of lineRows.value) {
      if (row.lockedBasis === 'amount') {
        row.selected = row.remainingAmountMinor > 0;
        row.entryMode = 'amount';
        row.creditQty = 0;
        row.creditAmountMinor = row.remainingAmountMinor > 0 ? row.remainingAmountMinor : null;
        row.creditAmountDisplay =
          row.remainingAmountMinor > 0
            ? String(minorUnitsToAmount(row.remainingAmountMinor, currency))
            : '';
      } else {
        row.selected = row.remainingQty > 0;
        row.entryMode = 'qty';
        row.creditQty = row.remainingQty;
        row.creditAmountMinor = null;
        row.creditAmountDisplay = '';
      }
      revalidateRow(row);
    }
    // Wave 15 — Full-mode prefills are wizard-chosen values: clamp them to the invoice cap too.
    clampAllPrefillsToInvoiceCap();
  }
  markFormDirty();
}

/**
 * Wave 15 (finance feedback): the invoice-level cap, and how much of it the REST of the draft
 * (every row except `except`) already consumes. `effectiveMax*For` = what this one row can still
 * take — the number prefills are clamped to and the inline invalid message quotes, so the wizard
 * never defaults to (or recommends) a value the preview endpoint is guaranteed to reject.
 */
function invoiceCapMinor(): number {
  return props.eligibility?.remaining_creditable_minor ?? 0;
}

function draftTotalMinorExcept(except: LineRow | null): number {
  let total = 0;
  for (const row of lineRows.value) {
    if (row !== except) total += lineTotalMinor(row);
  }
  return total;
}

function effectiveMaxAmountMinorFor(row: LineRow): number {
  return Math.max(0, Math.min(row.remainingAmountMinor, invoiceCapMinor() - draftTotalMinorExcept(row)));
}

function effectiveMaxQtyFor(row: LineRow): number {
  if (row.unit_amount_minor <= 0) return row.remainingQty;
  const capLeft = Math.max(0, invoiceCapMinor() - draftTotalMinorExcept(row));
  return Math.min(row.remainingQty, Math.floor(capLeft / row.unit_amount_minor));
}

/**
 * Wave 15 — prefill clamp. Applies ONLY to values the wizard chose itself (open / Full-mode /
 * re-select prefills), never to user-typed input, which keeps the reject-don't-clamp rule for
 * typing intact. Runs row-by-row so each clamp sees the previous rows' already-clamped totals —
 * across multiple lines the prefills collectively fit the invoice cap.
 */
function clampRowPrefillToInvoiceCap(row: LineRow): void {
  if (!row.selected) return;
  const currency = props.eligibility?.currency ?? 'USD';
  if (row.entryMode === 'amount') {
    if (row.creditAmountMinor === null) return;
    const max = effectiveMaxAmountMinorFor(row);
    if (row.creditAmountMinor > max) {
      row.creditAmountMinor = max > 0 ? max : null;
      row.creditAmountDisplay = max > 0 ? String(minorUnitsToAmount(max, currency)) : '';
    }
    return;
  }
  const maxQty = effectiveMaxQtyFor(row);
  if (row.creditQty > maxQty) {
    row.creditQty = Math.max(0, maxQty);
  }
}

function clampAllPrefillsToInvoiceCap(): void {
  for (const row of lineRows.value) {
    clampRowPrefillToInvoiceCap(row);
    revalidateRow(row);
  }
}

/**
 * AB#23070: changing a discount row shifts the invoice-cap headroom of EVERY other row (dropping
 * the discount can push the rest over the cap), so re-check them all; any other row only re-checks
 * itself, as before.
 */
function revalidateAfterChange(row: LineRow): void {
  if (isNegativeLine(row)) {
    for (const other of lineRows.value) revalidateRow(other);
    return;
  }
  revalidateRow(row);
}

/** Re-runs {@link validateLineEntry} for one row and stores the result on it. */
function revalidateRow(row: LineRow): void {
  const currency = props.eligibility?.currency ?? 'USD';
  const result = validateLineEntry({
    entryMode: row.entryMode,
    selected: row.selected,
    parsedQty: row.creditQty,
    remainingQty: row.remainingQty,
    parsedAmountMinor: row.creditAmountMinor ?? 0,
    remainingAmountMinor: row.remainingAmountMinor,
    currency,
    effectiveMaxQty: effectiveMaxQtyFor(row),
    effectiveMaxAmountMinor: effectiveMaxAmountMinorFor(row),
  });
  row.invalid = !result.valid;
  row.invalidMessage = result.message;
}

function onSelectToggle(row: LineRow, checked: boolean): void {
  if (row.remainingQty <= 0) {
    // Fully credited — can't be re-selected (defensive; checkbox is also disabled).
    row.selected = false;
    revalidateRow(row);
    markFormDirty();
    return;
  }
  row.selected = checked;
  if (!checked) {
    // Reset to the row's home mode — amount-locked rows must never re-enter qty mode.
    row.entryMode = row.lockedBasis === 'amount' ? 'amount' : 'qty';
    row.creditQty = 0;
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
  } else if (row.creditQty === 0 && row.entryMode === 'qty') {
    row.creditQty = row.remainingQty;
    // Wave 15 — re-select prefill is wizard-chosen: clamp it to what the invoice has left.
    clampRowPrefillToInvoiceCap(row);
  }
  revalidateAfterChange(row);
  markFormDirty();
}

/**
 * WS2 — entry mode toggle. Switching modes clears the OTHER field's value (and display, for
 * amount) explicitly so stale state from the inactive mode can never leak into the payload.
 */
function onEntryModeChange(row: LineRow, next: LineEntryMode): void {
  if (row.entryMode === next) return;
  // Stripe basis lock — a line credited by one basis can never switch to the other (defensive;
  // the toggle buttons are also disabled with a tooltip when locked).
  if (row.lockedBasis === 'quantity' && next === 'amount') return;
  if (row.lockedBasis === 'amount' && next === 'qty') return;
  row.entryMode = next;
  if (next === 'qty') {
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
  } else {
    row.creditQty = 0;
  }
  revalidateRow(row);
  markFormDirty();
}

/**
 * Discoverability (AB#21269 follow-up): a disabled qty/amount input reads as "dead" — finance
 * users didn't find the By qty | By amount toggle. When the OTHER mode's input is disabled only
 * because of the current entry mode (not Full mode, not deselected, not capped, not basis-locked),
 * clicking its cell switches the row to that mode and focuses the input.
 */
function canActivateQtyMode(row: LineRow): boolean {
  return (
    mode.value !== 'full' &&
    row.selected &&
    row.entryMode === 'amount' &&
    row.remainingQty > 0 &&
    row.lockedBasis !== 'amount'
  );
}

function canActivateAmountMode(row: LineRow): boolean {
  return (
    mode.value !== 'full' &&
    row.selected &&
    row.entryMode === 'qty' &&
    row.remainingAmountMinor > 0 &&
    row.lockedBasis !== 'quantity'
  );
}

function focusRowInput(testId: 'line-qty-input' | 'line-amount-input', lineId: string): void {
  void nextTick(() => {
    const input = document.querySelector<HTMLInputElement>(
      `[data-testid="${testId}"][data-line-id="${lineId}"]`,
    );
    input?.focus();
  });
}

function onQtyCellClick(row: LineRow): void {
  if (!canActivateQtyMode(row)) return;
  onEntryModeChange(row, 'qty');
  focusRowInput('line-qty-input', row.id);
}

function onAmountCellClick(row: LineRow): void {
  if (!canActivateAmountMode(row)) return;
  onEntryModeChange(row, 'amount');
  focusRowInput('line-amount-input', row.id);
}

function onQtyInput(row: LineRow, raw: string): void {
  // Reject-over-cap (WS2): the raw text stays in the input untouched; parseQtyInput only
  // NaN/empty-string-guards (rounds-to-zero-safe), it never clamps to remainingQty.
  row.creditQty = parseQtyInput(raw);
  revalidateAfterChange(row);
  markFormDirty();
}

function onAmountInput(row: LineRow, raw: string): void {
  row.creditAmountDisplay = raw;
  const trimmed = raw.trim();
  if (trimmed === '') {
    row.creditAmountMinor = null;
    revalidateRow(row);
    markFormDirty();
    return;
  }
  const currency = props.eligibility?.currency ?? 'USD';
  const minor = amountToMinorUnits(Number(raw), currency);
  if (minor <= 0) {
    // Rounds-to-zero fix: a value that parses down to 0 minor units (e.g. "0.001") must not
    // leave a stale-looking display behind while the numeric state is cleared — clear both.
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
    revalidateRow(row);
    markFormDirty();
    return;
  }
  // Reject-over-cap (WS2): NOT clamped to remainingAmountMinor — revalidateRow flags it invalid
  // instead so the user's typed number is never silently rewritten.
  row.creditAmountMinor = minor;
  revalidateRow(row);
  markFormDirty();
}

function lineTotalMinor(row: LineRow): number {
  if (!row.selected) return 0;
  if (row.entryMode === 'amount') {
    return row.creditAmountMinor ?? 0;
  }
  // Full mode pre-fills creditQty = remainingQty (via switchMode / buildLineRow), so we just
  // use creditQty consistently across both modes — single source of truth.
  return row.creditQty * row.unit_amount_minor;
}

/**
 * AB#23070: a discount line (negative unit, e.g. "kit/firewall discount" -1,119.00). It counts
 * NEGATIVE in every total and in the invoice-cap clamp, and is credited by quantity only: Stripe
 * amount credits are positive, and eligibility reports its remaining amount as 0.
 */
function isNegativeLine(row: LineRow): boolean {
  return row.unit_amount_minor < 0;
}

/** Tooltip for the per-row "By amount" toggle. */
function amountToggleTitle(row: LineRow): string {
  if (isNegativeLine(row)) {
    return 'Discount lines are credited by quantity only, together with the lines they reduce.';
  }
  if (row.lockedBasis === 'quantity') {
    return 'This line was previously credited by quantity — Stripe locks its further credits to By qty.';
  }
  return 'Credit a specific amount for this line instead of whole quantities.';
}

const totalMinor = computed(() => {
  let total = 0;
  for (const row of lineRows.value) total += lineTotalMinor(row);
  return total;
});

/** No selected line contributes anything — distinct from a net total of 0 or below (AB#23070). */
const noLineToCredit = computed(() =>
  lineRows.value.every((row) => !row.selected || lineTotalMinor(row) === 0)
);

/** Any invoice-line row currently invalid — gates the footer's concrete total. */
const anyRowInvalid = computed(() => lineRows.value.some((row) => row.invalid));

/** Legacy aggregate cap check (pre-tax local total vs. `remaining_creditable_minor`) — used
 * verbatim when `previewSupported !== true` (404 feature-detect / still detecting). */
const isOverCap = computed(() => {
  const cap = props.eligibility?.remaining_creditable_minor ?? 0;
  return totalMinor.value > cap;
});

/** WS1 — preview-backed cap check: tax-inclusive preview total vs. the authoritative cap. */
const previewIsOverCap = computed(() => {
  if (!preview.value) return false;
  const cap = props.eligibility?.max_creditable_minor ?? props.eligibility?.remaining_creditable_minor ?? 0;
  return preview.value.total_minor > cap;
});

/** Whichever cap check is active for the current backend (preview-backed vs. legacy). */
const effectiveIsOverCap = computed(() => (
  previewSupported.value === true ? previewIsOverCap.value : isOverCap.value
));

/** Tax-inclusive preview total when available, else the local pre-tax total (legacy fallback). */
const effectiveTotalMinor = computed(() => (
  previewSupported.value === true && preview.value ? preview.value.total_minor : totalMinor.value
));

/** Point 8 (AB#22842): finance reads the footer's "Remaining creditable" as what will be LEFT
 * after this credit note, so it tracks the draft live: cap minus the current draft total,
 * floored at 0 (an over-cap draft is already blocked and flagged red elsewhere). While a row
 * is invalid the draft total is not trustworthy, so the full cap shows instead. AB#23070: a
 * draft that nets to 0 or below (a discount line alone) credits nothing and is blocked, so the
 * full cap shows too, never cap minus a negative draft (that read 110.00 on a 50.00 invoice). */
const remainingAfterDraftMinor = computed(() => {
  if (anyRowInvalid.value) return remainingCreditableMinor.value;
  const draft = Number(effectiveTotalMinor.value) || 0;
  if (draft <= 0) return remainingCreditableMinor.value;
  return Math.max(remainingCreditableMinor.value - draft, 0);
});

/** True while any invoice-line row is over its own remaining cap (WS2 reject-over-cap). */
const anyLineRowInvalid = computed(() => lineRows.value.some((row) => row.invalid));

/** AB#22842 — the internal note is required when the reason is Other. */
const internalNoteMissing = computed(
  () => internalReason.value === 'other' && internalNote.value.trim() === ''
);

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (!props.eligibility) return false;
  if (totalMinor.value <= 0) return false;
  if (anyLineRowInvalid.value) return false;
  if (internalNoteMissing.value) return false;
  if (previewSupported.value === true) {
    // WS1 — submit requires a fresh (non-stale, non-error) preview; no pre-tax fallback.
    if (previewState.value !== 'fresh') return false;
    if (previewIsOverCap.value) return false;
    return true;
  }
  // Legacy fallback (older backend / still feature-detecting) — old aggregate-cap gating.
  if (isOverCap.value) return false;
  return true;
});

/**
 * Whether ANY line can still take an amount-based credit. False when every line is either fully
 * credited or basis-locked to quantity (Stripe locks a line to the credit basis first used on
 * it) — in that case the "Use Credit amount instead" affordance is disabled with an explainer.
 */
const amountModeAvailable = computed(() =>
  lineRows.value.some((row) => row.remainingAmountMinor > 0 && row.lockedBasis !== 'quantity'),
);

/** WS6 — statement shown in the confirm step, keyed on the chosen settlement (AB#22842); the
 * unpaid path keeps the payment-path statement (no settlement choice there). */
const confirmStatement = computed(() => {
  if (!props.eligibility) return '';
  if (settlementOptions.value.length > 0 && settlementOption.value) {
    return settlementPreCreateStatement(
      settlementOption.value,
      effectiveTotalMinor.value,
      props.eligibility.currency
    );
  }
  return paymentPathPreCreateStatement(
    props.eligibility.payment_path,
    effectiveTotalMinor.value,
    props.eligibility.currency,
    { partiallyPaid: props.eligibility.partially_paid ?? false },
  );
});

const confirmButtonLabel = computed(() => {
  const settlement = settlementOption.value;
  if (settlement === 'card_refund') {
    return `Confirm refund of ${formatMinor(effectiveTotalMinor.value)} ${props.eligibility?.currency ?? ''}`.trim();
  }
  if (settlement === 'bank_refund') return 'Confirm credit (bank refund to follow)';
  if (settlement === 'no_refund') return 'Confirm credit without refund';
  return 'Confirm';
});

function close(): void {
  if (submitting.value) return;
  // If the user dismisses (X / Cancel / backdrop) AFTER a successful create, propagate
  // the credit row so the parent still refreshes — otherwise the order would render with
  // stale data even though the CN exists on Stripe + in our ledger.
  if (state.value === 'success' && submitResult.value) {
    emit('credit-note-created', {
      credit: submitResult.value.rawCreditRow,
      payment_path: submitResult.value.paymentPath,
      credit_note_total_minor: submitResult.value.totalMinor,
    });
  }
  emit('update:modelValue', false);
}

function buildPayload(): Record<string, unknown> {
  const invoiceLineItems: Array<Record<string, unknown>> = [];
  for (const row of lineRows.value) {
    if (!row.selected) continue;
    // AB#23070: a discount line has a NEGATIVE total and must still be sent (by quantity); only
    // rows with nothing to credit are skipped.
    if (lineTotalMinor(row) === 0) continue;
    const entry: Record<string, unknown> = { invoice_line_item: row.id };
    if (row.entryMode === 'amount' && row.creditAmountMinor !== null) {
      entry.amount = row.creditAmountMinor;
    } else if (row.creditQty > 0) {
      entry.quantity = row.creditQty;
    }
    invoiceLineItems.push(entry);
  }

  // AB#22842: free-form/custom lines are gone, the internal reason catalog replaces the raw
  // Stripe reason, and the chosen settlement travels with the payload (paid paths only).
  return {
    order_item_invoice_id: props.orderItemInvoiceId,
    mode: mode.value,
    invoice_line_items: invoiceLineItems,
    internal_reason: internalReason.value,
    internal_note: internalNote.value.trim() || null,
    settlement_option: settlementOptions.value.length > 0 ? settlementOption.value : null,
    memo: memo.value.trim() || null,
    idempotency_key: idempotencyKey.value,
  };
}

/** Same shape as {@link buildPayload}, minus `idempotency_key` — the preview endpoint's payload.
 * AB#22842: the reason/note/settlement/memo fields never change the preview totals, so they are
 * stripped too — otherwise every radio click or keystroke in the note would re-run the preview
 * and (via the payload-signature watch) briefly block submit for nothing. */
function buildPreviewPayload(): Record<string, unknown> {
  const payload = buildPayload();
  delete payload.idempotency_key;
  delete payload.internal_reason;
  delete payload.internal_note;
  delete payload.settlement_option;
  delete payload.memo;
  return payload;
}

/**
 * WS1 — debounced preview scheduler. `immediate: true` (used on wizard open + Retry) skips the
 * debounce and fires right away. No-ops entirely once a 404 has feature-detected the backend as
 * not supporting preview for this modal-open session.
 */
function schedulePreview(immediate = false): void {
  if (previewDebounceTimer) {
    clearTimeout(previewDebounceTimer);
    previewDebounceTimer = null;
  }
  if (previewSupported.value === false) return;
  if (!props.eligibility) return;
  if (totalMinor.value <= 0 || anyLineRowInvalid.value) {
    // Nothing valid to preview yet — clear any stale preview rather than showing an error.
    preview.value = null;
    previewState.value = 'idle';
    previewErrorInfo.value = null;
    return;
  }
  previewState.value = 'pending';
  if (immediate) {
    void runPreview();
    return;
  }
  previewDebounceTimer = setTimeout(() => {
    void runPreview();
  }, 400);
}

async function runPreview(): Promise<void> {
  if (!props.eligibility) return;
  const mySequence = ++previewSequence;
  const payload = buildPreviewPayload();
  try {
    const result = await previewCreditNote(props.orderId, payload);
    if (mySequence !== previewSequence) return; // Stale — a newer request has already landed.
    previewSupported.value = true;
    preview.value = result;
    previewState.value = 'fresh';
    previewErrorInfo.value = null;
  } catch (err) {
    if (mySequence !== previewSequence) return;
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404 && previewSupported.value === null) {
      // Older backend — feature-detect once per modal open, fall back to the legacy footer.
      previewSupported.value = false;
      preview.value = null;
      previewState.value = 'idle';
      previewErrorInfo.value = null;
      return;
    }
    previewSupported.value = true;
    preview.value = null;
    previewState.value = 'error';
    previewErrorInfo.value = creditNoteErrorFromResponse(err);
  }
}

/** Re-runs the debounced preview whenever the (idempotency-key-excluded) payload shape changes. */
const previewPayloadSignature = computed(() => (
  props.eligibility ? JSON.stringify(buildPreviewPayload()) : ''
));

watch(previewPayloadSignature, () => {
  if (!props.modelValue) return;
  schedulePreview();
});

onUnmounted(() => {
  if (previewDebounceTimer) clearTimeout(previewDebounceTimer);
});

/**
 * WS6 — "Create credit note" click handler. card_refund / out_of_band require an explicit
 * confirm step (refund / off-platform money movement); unpaid (and any other/unknown path)
 * submits directly in one click.
 */
function onCreateClick(): void {
  if (!canSubmit.value) return;
  const path = props.eligibility?.payment_path ?? null;
  if (path === 'card_refund' || path === 'out_of_band') {
    state.value = 'confirming';
    if (previewSupported.value === true && previewState.value !== 'fresh') {
      schedulePreview(true);
    }
    return;
  }
  void submit();
}

/**
 * WS3 — auto-tax explainer action: switches the first line row with creditable amount left
 * into amount mode (selecting it first if needed) and focuses its amount input, so the user
 * can credit an arbitrary total without a (server-rejected) free-form line.
 */
function switchFirstEligibleRowToAmountMode(): void {
  // Basis-locked-to-quantity rows can never take an amount credit (Stripe rule) — skip them.
  const target = lineRows.value.find(
    (row) => row.remainingAmountMinor > 0 && row.lockedBasis !== 'quantity',
  );
  if (!target) return;
  if (!target.selected) target.selected = true;
  if (mode.value === 'full') {
    // Full mode hides the per-row entry-mode toggle — switch to Partial so amount mode applies.
    switchMode('partial');
  }
  onEntryModeChange(target, 'amount');
  markFormDirty();
  void nextTick(() => {
    const input = document.querySelector<HTMLInputElement>(
      `[data-testid="line-amount-input"][data-line-id="${target.id}"]`,
    );
    input?.focus();
  });
}

async function submit(): Promise<void> {
  if (!canSubmit.value || !props.eligibility) return;
  if (props.orderItemInvoiceId <= 0) {
    showToast('Internal: invoice ledger id missing — close the wizard and re-open it.', 'error');
    return;
  }

  // Smart idempotency: rotate key only when the form changed since the last submit attempt.
  // A pure retry of the identical payload keeps the same key so Stripe dedupes (AC: double-
  // click → one CN). After a user edit post-error, a new key signals a genuinely new attempt.
  if (formDirtyAfterLastSubmit.value) {
    idempotencyKey.value = makeUuid();
  }

  state.value = 'submitting';
  try {
    const payload = buildPayload();
    // Backend `sendJsonResponse($message, $data)` wraps the payload under `result` (NOT `data`).
    // Shape: `{ message, result: { credit, payment_path, credit_note_total_minor }, errors: [] }`.
    const response = await http.post<{
      message: string;
      result: {
        credit: Record<string, unknown>;
        payment_path: string;
        settlement_option?: string | null;
        credit_note_total_minor: number;
      };
    }>(`customer-orders/${props.orderId}/credit-notes`, payload);

    const data = response.data.result;
    const paymentPath = data?.payment_path ?? props.eligibility.payment_path ?? null;
    const totalMinor = Number(data?.credit_note_total_minor ?? 0);
    const credit = (data?.credit ?? {}) as Record<string, unknown>;
    submitResult.value = {
      paymentPath,
      // AB#22842 — the backend echoes the applied settlement; older backends omit it, in which
      // case what the user picked locally is the best available record.
      settlementOption: data?.settlement_option ?? settlementOption.value ?? null,
      totalMinor: Number.isFinite(totalMinor) ? totalMinor : 0,
      currency: props.eligibility.currency,
      creditNoteId: typeof credit.stripe_credit_note_id === 'string' ? credit.stripe_credit_note_id : null,
      rawCreditRow: credit,
    };
    // AB#22842 (Elisabete's point 8) — the footer figure moves the moment the credit lands.
    remainingCreditableMinor.value = Math.max(
      0,
      remainingCreditableMinor.value - (Number.isFinite(totalMinor) ? totalMinor : 0)
    );
    state.value = 'success';
    formDirtyAfterLastSubmit.value = false;
    // Note: emit('credit-note-created') fires when the user dismisses the success card via Done
    // so the parent doesn't see UI updates while the success card is still on screen.
  } catch (err) {
    const info = creditNoteErrorFromResponse(err);
    const expectedPath = props.eligibility.payment_path;
    submitError.value = {
      message: info.message,
      detail: info.detail,
      // `refund_failed` from the backend is authoritative; keep the message-sniffing heuristic
      // as a fallback for older/unmapped error shapes that still describe a refund failure.
      isRefundFailure: info.code === 'refund_failed' || isLikelyRefundFailure(info.message, expectedPath),
    };
    state.value = 'error';
    formDirtyAfterLastSubmit.value = false;
    // No success toast, no emit — the UI never refreshes as if credited on a failed create
    // (AC: failed create does not refresh UI as if credited).
  }
}

/** Success "Done" → delegate to `close()` so dismiss-via-X / Cancel / Done all propagate identically. */
function onSuccessDone(): void {
  close();
}

/**
 * Error "Edit & retry" → back to idle so the user can adjust before re-submitting.
 * Marks the form dirty so the next submit rotates the idempotency key — otherwise Stripe
 * would return the SAME cached error response for an unchanged-payload retry (idempotency
 * caches failures too). True double-click dedupe on the *first* submit is still preserved
 * via the `state === 'submitting'` + `canSubmit` button-disable guard.
 */
function onErrorRetry(): void {
  state.value = 'idle';
  formDirtyAfterLastSubmit.value = true;
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      idempotencyKey.value = makeUuid();
      resetRowsFromEligibility();
      // Default to Partial mode when the invoice already has credit notes against it.
      // Full mode would re-credit all lines at full qty (pre-tax sum) and exceed the
      // post-prior-credits cap — the user must pick what to credit from what's left.
      const hasPriorCns = (props.eligibility?.existing_credit_notes?.length ?? 0) > 0;
      mode.value = hasPriorCns ? 'partial' : 'full';
    }
  },
  { immediate: true },
);

watch(() => props.eligibility, () => {
  if (props.modelValue) {
    resetRowsFromEligibility();
  }
});

/**
 * Smart idempotency rotation: handlers that mutate user-controlled state call `markFormDirty()`,
 * and reactive fields with v-model (reason, memo) are watched individually. Using explicit hooks
 * avoids the false-positive a `formSignature` watch fires when `resetRowsFromEligibility()`
 * mutates the same state during open / eligibility-prop changes.
 */
function markFormDirty(): void {
  if (state.value === 'submitting') return;
  // WS6 — an edit while the confirm strip is showing invalidates that confirmation (the total
  // it quoted may no longer be accurate); drop back to idle so the user re-triggers it.
  if (state.value === 'confirming') state.value = 'idle';
  formDirtyAfterLastSubmit.value = true;
}

watch(internalReason, () => markFormDirty());
watch(internalNote, () => markFormDirty());
watch(settlementOption, () => markFormDirty());
watch(memo, () => markFormDirty());

function formatMinor(minor: number): string {
  const currency = props.eligibility?.currency ?? 'USD';
  return formatMinorAsDisplay(minor, currency);
}

/** Stripe `created` is a unix-seconds timestamp — render as locale date+time. */
function formatCnCreated(unixSeconds: number): string {
  if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) return '';
  try {
    return new Date(unixSeconds * 1000).toLocaleString();
  } catch {
    return '';
  }
}

/**
 * Settlement tag for a credit-note history entry (AB#21269) — mirrors the ledger's merged pill.
 * AB#22842: prefers the PERSISTED settlement choice when present, which is the only way to tell
 * "Refund manually by bank" from "No refund" (both are out-of-band on Stripe's side). Returns
 * null (no tag) when the settlement is unknown (old backend / no local row for the CN) and for
 * pre-payment CNs, where the entry already reads naturally without a tag.
 */
function historySettlementTag(cn: {
  refund_amount?: number | string | null;
  out_of_band_amount?: number | string | null;
  settlement_option?: string | null;
}): { label: string; class: string; tooltip: string } | null {
  if (cn.settlement_option === 'bank_refund') {
    return {
      label: 'Bank refund',
      class: 'border-amber-300 bg-amber-100 text-amber-900',
      tooltip: 'Recorded as credited outside of Stripe: finance transfers the money by bank.',
    };
  }
  if (cn.settlement_option === 'no_refund') {
    return {
      label: 'No refund',
      class: 'border-slate-300 bg-surface-2 text-ink-soft',
      tooltip: 'Recorded as credited outside of Stripe. No money moves.',
    };
  }
  const settlement = creditNoteSettlement(cn.refund_amount, cn.out_of_band_amount);
  if (settlement === 'card_refund') {
    return {
      label: 'Refunded',
      class: 'border-indigo-200 bg-indigo-50 text-indigo-800',
      tooltip: 'Created after the invoice was paid — this amount was refunded to the customer’s card.',
    };
  }
  if (settlement === 'out_of_band') {
    return {
      label: 'Out-of-band',
      class: 'border-amber-300 bg-amber-100 text-amber-900',
      tooltip: 'Created after the invoice was paid — recorded for manual settlement by finance.',
    };
  }
  return null;
}

/** Map Stripe `reason` enum to label (matches the dropdown in the wizard). */
function formatCnReason(reason: string | null | undefined): string {
  if (!reason) return '';
  const match = CREDIT_NOTE_REASONS.find((r) => r.value === reason);
  return match ? match.label : reason;
}

defineExpose({
  totalMinor,
  isOverCap,
  buildPayload,
  buildPreviewPayload,
  canSubmit,
  anyLineRowInvalid,
  lineRows,
  state,
  preview,
  previewState,
  previewErrorInfo,
  previewSupported,
  effectiveIsOverCap,
  effectiveTotalMinor,
  confirmStatement,
  confirmButtonLabel,
  onCreateClick,
  schedulePreview,
  switchFirstEligibleRowToAmountMode,
  invoiceStatusChip,
  paymentPathHint,
  paymentPathTooltip,
  amountModeAvailable,
  canActivateQtyMode,
  canActivateAmountMode,
  onQtyCellClick,
  onAmountCellClick,
  // AB#22842
  settlementOptions,
  settlementOption,
  internalReason,
  internalNote,
  internalNoteMissing,
  remainingCreditableMinor,
  unpaidOverflowMinor,
  selectableRowCount,
  allSelectableRowsSelected,
  someSelectableRowsSelected,
  onSelectAllToggle,
});
</script>
